import React, { createContext, useContext, useState, useEffect } from 'react';
import { contactInfo as defaultContactInfo, stats as defaultStats, testimonials as defaultTestimonials, services as defaultServices } from '../data';
import { useLanguage } from './LanguageContext';

const CmsContentContext = createContext({
  getImage: (key, fallback) => fallback,
  getText: (key, field, fallback) => fallback,
  getHtml: (key, field, fallback) => fallback,
  getPageText: (page, section, field, fallback) => fallback,
  getPageHtml: (page, section, field, fallback) => fallback,
  getPageImage: (page, section, fallback) => fallback,
  getPoints: (key, fallback = []) => fallback,
  getPagePoints: (page, section, fallback = []) => fallback,
  images: {},
  content: {},
  pages: {},
  companyInfo: defaultContactInfo,
  stats: defaultStats,
  testimonials: defaultTestimonials,
  services: defaultServices,
  loading: true,
});

/**
 * Helper to parse a multiline points field ("Title: Text" or bullet lines).
 */
export function parsePoints(rawPoints, language = 'da', fallback = []) {
  if (!rawPoints) return fallback;
  let text = '';
  if (typeof rawPoints === 'string') {
    text = rawPoints;
  } else if (typeof rawPoints === 'object') {
    if (rawPoints[language] && typeof rawPoints[language] === 'string' && rawPoints[language].trim()) {
      text = rawPoints[language];
    } else if (language !== 'da' && fallback && fallback.length > 0) {
      return fallback;
    } else {
      text = rawPoints.da || Object.values(rawPoints)[0] || '';
    }
  }
  if (!text || typeof text !== 'string') return fallback;

  // Check if HTML list items (from RICHTEXT_LEXICAL)
  if (text.includes('<li')) {
    const liMatches = [...text.matchAll(/<li[^>]*>(.*?)<\/li>/gi)];
    if (liMatches.length > 0) {
      return liMatches.map((m) => {
        const itemText = m[1].replace(/<[^>]+>/g, '').trim();
        const colonIdx = itemText.indexOf(':');
        if (colonIdx > 0) {
          return {
            title: itemText.slice(0, colonIdx).trim(),
            text: itemText.slice(colonIdx + 1).trim(),
            full: itemText,
          };
        }
        return {
          title: '',
          text: itemText,
          full: itemText,
        };
      });
    }
  }

  // Normalize HTML tags to newlines
  const normalized = text.replace(/<\/?(p|br|div)[^>]*>/gi, '\n').replace(/<[^>]+>/g, '');

  let rawLines = normalized
    .split(/[\n\r•·]+/g)
    .map((line) => line.trim().replace(/^[-*]\s*/, ''))
    .filter(Boolean);

  // If all points collapsed to 1 line with multiple colons, split before "Title:"
  if (rawLines.length === 1 && (rawLines[0].match(/:/g) || []).length > 1) {
    const splitRegex = /(?:\.|\s|^)([A-ZÆØÅ][^:\n.]{2,30}):\s*/g;
    const matches = [...rawLines[0].matchAll(splitRegex)];
    if (matches.length > 1) {
      const result = [];
      for (let i = 0; i < matches.length; i++) {
        const title = matches[i][1].trim();
        const start = matches[i].index + matches[i][0].length;
        const end = (i + 1 < matches.length) ? matches[i + 1].index : rawLines[0].length;
        const itemText = rawLines[0].slice(start, end).replace(/^\.\s*/, '').trim();
        result.push({
          title,
          text: itemText,
          full: `${title}: ${itemText}`,
        });
      }
      if (result.length > 0) return result;
    }
  }

  return rawLines.map((line) => {
    const colonIdx = line.indexOf(':');
    if (colonIdx > 0) {
      return {
        title: line.slice(0, colonIdx).trim(),
        text: line.slice(colonIdx + 1).trim(),
        full: line,
      };
    }
    return {
      title: '',
      text: line,
      full: line,
    };
  });
}

/**
 * Helper to optionally format Auxo CDN images with Cloudflare on-the-fly resizing.
 */
export function getOptimizedImageUrl(url, options = {}) {
  if (!url || typeof url !== 'string') return url;

  // Only rewrite Auxo CDN URLs and avoid double-wrapping
  if (url.startsWith('https://assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
    const parts = ['format=auto'];
    if (options.quality) parts.push(`quality=${options.quality}`);
    if (options.width) parts.push(`width=${options.width}`);
    if (options.height) parts.push(`height=${options.height}`);
    if (options.fit) parts.push(`fit=${options.fit}`);

    const config = parts.join(',');
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/${config}/`);
  }

  return url;
}

export function CmsImagesProvider({ children }) {
  const [images, setImages] = useState({});
  const [content, setContent] = useState({});
  const [pages, setPages] = useState({});
  const [companyInfo, setCompanyInfo] = useState(defaultContactInfo);
  const [stats, setStats] = useState(defaultStats);
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  const [services, setServices] = useState(defaultServices);
  const [forsideServices, setForsideServices] = useState(null);
  const [tjenesterServices, setTjenesterServices] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    let isMounted = true;

    async function loadCmsData() {
      try {
        const [
          // 1. Dedikerede sidesamlinger
          forsideRes,
          omOsRes,
          privatRes,
          erhvervRes,
          tjenesterRes,
          kontaktRes,
          galleriRes,
          // 2. Tværgående samlinger
          companyRes,
          testimonialsRes,
        ] = await Promise.allSettled([
          fetch('https://api.dev.auxo.dk/public/content/forside').then(r => r.ok ? r.json() : null),
          fetch('https://api.dev.auxo.dk/public/content/om_os').then(r => r.ok ? r.json() : null),
          fetch('https://api.dev.auxo.dk/public/content/privatrengoring').then(r => r.ok ? r.json() : null),
          fetch('https://api.dev.auxo.dk/public/content/erhvervsrengoring').then(r => r.ok ? r.json() : null),
          fetch('https://api.dev.auxo.dk/public/content/tjenester_side').then(r => r.ok ? r.json() : null),
          fetch('https://api.dev.auxo.dk/public/content/kontakt_side').then(r => r.ok ? r.json() : null),
          fetch('https://api.dev.auxo.dk/public/content/galleri_side').then(r => r.ok ? r.json() : null),
          fetch('https://api.dev.auxo.dk/public/content/virksomhedsoplysninger').then(r => r.ok ? r.json() : null),
          fetch('https://api.dev.auxo.dk/public/content/kundeudtalelser').then(r => r.ok ? r.json() : null),
        ]);

        if (!isMounted) return;

        const mappedImages = {};
        const mappedContent = {};
        const mappedPages = {
          forside: {},
          om_os: {},
          privatrengoring: {},
          erhvervsrengoring: {},
          tjenester_side: {},
          kontakt_side: {},
          galleri_side: {},
        };

        // Behandling af de dedikerede sidesamlinger (højeste prioritet)
        const processPageItems = (items, pageKey, keyAliases = {}) => {
          if (!Array.isArray(items)) return;
          items.forEach((item) => {
            const noegle = item.noegle;
            if (!noegle) return;

            mappedPages[pageKey][noegle] = item;
            mappedContent[`${pageKey}_${noegle}`] = item;
            mappedContent[`${pageKey}.${noegle}`] = item;

            // Tilføj specifikke nøgle-aliasser for bagudkompatibilitet
            if (keyAliases[noegle]) {
              const aliases = Array.isArray(keyAliases[noegle]) ? keyAliases[noegle] : [keyAliases[noegle]];
              aliases.forEach((alias) => {
                mappedContent[alias] = item;
              });
            }

            // Hent eventuelt sektionsbillede direkte fra posten
            const img = item.billede;
            if (img) {
              const src = Array.isArray(img) ? img[0] : img;
              if (src) {
                mappedImages[`${pageKey}_${noegle}`] = src;
                mappedImages[`${pageKey}.${noegle}`] = src;
                if (keyAliases[noegle + '_img']) {
                  const imgAliases = Array.isArray(keyAliases[noegle + '_img']) ? keyAliases[noegle + '_img'] : [keyAliases[noegle + '_img']];
                  imgAliases.forEach((alias) => {
                    mappedImages[alias] = src;
                  });
                }
              }
            }

            // Hent specifikke under-billeder som billede_1..billede_4
            [1, 2, 3, 4].forEach((num) => {
              const subField = item[`billede_${num}`];
              if (subField) {
                const subSrc = Array.isArray(subField) ? subField[0] : subField;
                if (subSrc) {
                  mappedImages[`${pageKey}_${noegle}_${num}`] = subSrc;
                  mappedImages[`${pageKey}_${noegle}_billede_${num}`] = subSrc;
                }
              }
            });

            // Særlig håndtering for forside fokus billeder (vaerdier sektionen)
            if (pageKey === 'forside' && noegle === 'vaerdier') {
              const getB = (num, fallbackIdx) => {
                const f = item[`billede_${num}`];
                if (f && Array.isArray(f) && f[0]) return f[0];
                if (f && typeof f === 'string') return f;
                if (Array.isArray(img) && img[fallbackIdx]) return img[fallbackIdx];
                return null;
              };
              const b1 = getB(1, 0);
              const b2 = getB(2, 1);
              const b3 = getB(3, 2);
              const b4 = getB(4, 3);
              if (b1) mappedImages['home_focus_1'] = b1;
              if (b2) mappedImages['home_focus_2'] = b2;
              if (b3) mappedImages['home_focus_3'] = b3;
              if (b4) mappedImages['home_focus_4'] = b4;
            }
          });
        };

        // Forside
        if (forsideRes.status === 'fulfilled' && Array.isArray(forsideRes.value)) {
          const singleItem = forsideRes.value.find(i => i.hero_overskrift || i.stat_1_vaerdi || i.titel === 'Forside') || forsideRes.value[0];
          if (singleItem && (singleItem.hero_overskrift || singleItem.hero_tekst || singleItem.stat_1_vaerdi)) {
            mappedPages['forside'] = { ...singleItem, ...mappedPages['forside'] };
            mappedContent['forside'] = singleItem;

            const heroImg = singleItem.hero_billede ? (Array.isArray(singleItem.hero_billede) ? singleItem.hero_billede[0] : singleItem.hero_billede) : null;
            if (heroImg) {
              mappedImages['home_hero'] = heroImg;
              mappedImages['forside_hero'] = heroImg;
              mappedImages['forside_hero_billede'] = heroImg;
            }

            if (Array.isArray(singleItem.vaerdier_billeder) && singleItem.vaerdier_billeder.length > 0) {
              if (singleItem.vaerdier_billeder[0]) mappedImages['home_focus_1'] = singleItem.vaerdier_billeder[0];
              if (singleItem.vaerdier_billeder[1]) mappedImages['home_focus_2'] = singleItem.vaerdier_billeder[1];
              if (singleItem.vaerdier_billeder[2]) mappedImages['home_focus_3'] = singleItem.vaerdier_billeder[2];
              if (singleItem.vaerdier_billeder[3]) mappedImages['home_focus_4'] = singleItem.vaerdier_billeder[3];
            }

            mappedContent['forside_hero'] = {
              underoverskrift: singleItem.hero_underoverskrift,
              overskrift: singleItem.hero_overskrift,
              tekst: singleItem.hero_tekst,
              knap_tekst: singleItem.hero_knap_tekst,
              knap_link: singleItem.hero_knap_link,
              knap_2_tekst: singleItem.hero_knap_2_tekst,
              knap_2_link: singleItem.hero_knap_2_link,
            };

            mappedContent['forside_ydelser'] = {
              underoverskrift: singleItem.ydelser_underoverskrift,
              overskrift: singleItem.ydelser_overskrift,
              tekst: singleItem.ydelser_tekst,
            };

            mappedContent['forside_fradrag'] = {
              tekst: singleItem.fradrag_tekst,
            };

            mappedContent['forside_vaerdier'] = {
              overskrift: singleItem.vaerdier_overskrift,
              tekst: singleItem.vaerdier_tekst,
              punkt_1_titel: singleItem.vaerdier_punkt_1_titel,
              punkt_1_tekst: singleItem.vaerdier_punkt_1_tekst,
              punkt_2_titel: singleItem.vaerdier_punkt_2_titel,
              punkt_2_tekst: singleItem.vaerdier_punkt_2_tekst,
              punkt_3_titel: singleItem.vaerdier_punkt_3_titel,
              punkt_3_tekst: singleItem.vaerdier_punkt_3_tekst,
            };

            mappedContent['forside_anmeldelser'] = {
              underoverskrift: singleItem.anmeldelser_underoverskrift,
              overskrift: singleItem.anmeldelser_overskrift,
              tekst: singleItem.anmeldelser_tekst,
            };

            mappedContent['forside_cta'] = {
              overskrift: singleItem.cta_overskrift,
              tekst: singleItem.cta_tekst,
              knap_tekst: singleItem.cta_knap_tekst,
              knap_link: singleItem.cta_knap_link,
              knap_2_tekst: singleItem.cta_knap_2_tekst,
              knap_2_link: singleItem.cta_knap_2_link,
            };

            // Nøgletal
            const statLabelsMap = {
              'Kommuner dækket': { da: 'Kommuner dækket', en: 'Municipalities covered' },
              'Kommune dækket': { da: 'Kommune dækket', en: 'Municipalities covered' },
              'Faste kunder': { da: 'Faste kunder', en: 'Regular customers' },
              'Kundetilfredshed': { da: 'Kundetilfredshed', en: 'Customer satisfaction' },
            };
            const resolveStatLabel = (rawLabel, fallbackDa, fallbackEn) => {
              if (!rawLabel) return { da: fallbackDa, en: fallbackEn };
              if (typeof rawLabel === 'object') {
                const da = rawLabel.da || fallbackDa;
                const en = rawLabel.en || statLabelsMap[da]?.en || fallbackEn;
                return { da, en };
              }
              return statLabelsMap[rawLabel] || { da: rawLabel, en: fallbackEn };
            };

            if (singleItem.stat_1_vaerdi || singleItem.stat_2_vaerdi || singleItem.stat_3_vaerdi) {
              const parsedStats = [];
              if (singleItem.stat_1_vaerdi) {
                parsedStats.push({ value: singleItem.stat_1_vaerdi, label: resolveStatLabel(singleItem.stat_1_tekst, 'Kommuner dækket', 'Municipalities covered') });
              }
              if (singleItem.stat_2_vaerdi) {
                parsedStats.push({ value: singleItem.stat_2_vaerdi, label: resolveStatLabel(singleItem.stat_2_tekst, 'Faste kunder', 'Regular customers') });
              }
              if (singleItem.stat_3_vaerdi) {
                parsedStats.push({ value: singleItem.stat_3_vaerdi, label: resolveStatLabel(singleItem.stat_3_tekst, 'Kundetilfredshed', 'Customer satisfaction') });
              }
              if (parsedStats.length > 0) {
                setStats(parsedStats);
              }
            }

            // Ydelser
            if (singleItem.ydelse_privat_titel || singleItem.ydelse_erhverv_titel) {
              const defPrivat = defaultServices.find((s) => s.id === 'privatrengoring');
              const defErhverv = defaultServices.find((s) => s.id === 'erhvervsrengoring');
              const defFlyt = defaultServices.find((s) => s.id === 'flytterengoring');

              const getServiceTitle = (fieldVal, defService) => {
                if (!fieldVal) return defService?.title || { da: '', en: '' };
                if (typeof fieldVal === 'object') {
                  return {
                    da: fieldVal.da || defService?.title?.da || '',
                    en: fieldVal.en || defService?.title?.en || fieldVal.da || '',
                  };
                }
                return { da: fieldVal, en: defService?.title?.en || fieldVal };
              };

              const getServiceDesc = (fieldVal, defService) => {
                if (!fieldVal) return language === 'en' ? defService?.description?.en : defService?.description?.da;
                if (typeof fieldVal === 'object') {
                  if (fieldVal[language]) return fieldVal[language].replace(/<[^>]*>/g, '').trim();
                  if (language === 'en' && defService?.description?.en) return defService.description.en;
                  return (fieldVal.da || '').replace(/<[^>]*>/g, '').trim();
                }
                if (language === 'en' && defService?.description?.en) return defService.description.en;
                return String(fieldVal).replace(/<[^>]*>/g, '').trim();
              };

              const getServiceDetails = (fieldVal, defService) => {
                if (language === 'en') {
                  if (fieldVal && typeof fieldVal === 'object' && fieldVal.en) return fieldVal.en;
                  return defService?.details?.en || fieldVal;
                }
                if (fieldVal && typeof fieldVal === 'object') return fieldVal.da || fieldVal;
                return fieldVal || defService?.details?.da;
              };

              const servicesList = [
                {
                  id: 'privatrengoring',
                  title: getServiceTitle(singleItem.ydelse_privat_titel, defPrivat),
                  description: getServiceDesc(singleItem.ydelse_privat_tekst, defPrivat),
                  details: getServiceDetails(singleItem.ydelse_privat_punkter, defPrivat),
                  image: (Array.isArray(singleItem.ydelse_privat_billede) ? singleItem.ydelse_privat_billede[0] : singleItem.ydelse_privat_billede) || '/images/private-cleaning.png',
                  link: '/privatrengoring',
                },
                {
                  id: 'erhvervsrengoring',
                  title: getServiceTitle(singleItem.ydelse_erhverv_titel, defErhverv),
                  description: getServiceDesc(singleItem.ydelse_erhverv_tekst, defErhverv),
                  details: getServiceDetails(singleItem.ydelse_erhverv_punkter, defErhverv),
                  image: (Array.isArray(singleItem.ydelse_erhverv_billede) ? singleItem.ydelse_erhverv_billede[0] : singleItem.ydelse_erhverv_billede) || '/images/business-cleaning.png',
                  link: '/erhvervsrengoring',
                },
                {
                  id: 'flytterengoring',
                  title: getServiceTitle(singleItem.ydelse_flyt_titel, defFlyt),
                  description: getServiceDesc(singleItem.ydelse_flyt_tekst, defFlyt),
                  details: getServiceDetails(singleItem.ydelse_flyt_punkter, defFlyt),
                  image: (Array.isArray(singleItem.ydelse_flyt_billede) ? singleItem.ydelse_flyt_billede[0] : singleItem.ydelse_flyt_billede) || '/images/move-cleaning.png',
                  link: '/tjenester',
                },
              ];
              setForsideServices(servicesList);
            }
          }

          processPageItems(forsideRes.value, 'forside', {
            hero: ['forside_hero'],
            hero_img: ['home_hero'],
            vaerdier: ['forside_vaerdier'],
            ydelser: ['forside_ydelser'],
            fradrag: ['forside_fradrag'],
            anmeldelser: ['forside_anmeldelser'],
            cta: ['forside_cta'],
          });

          // Udlæs nøgletal (stats) direkte fra forside samlingen
          const heroItem = forsideRes.value.find((i) => i.noegle === 'hero');
          if (heroItem) {
            const parsedStats = [];
            if (heroItem.punkter) {
              const pts = parsePoints(heroItem.punkter, language);
              pts.forEach((p) => {
                const val = p.title || p.text;
                const lbl = p.title ? p.text : p.title;
                if (val) {
                  parsedStats.push({
                    value: val,
                    label: { da: lbl, en: lbl },
                  });
                }
              });
            } else if (heroItem.punkt_1_titel || heroItem.punkt_1_tekst) {
              if (heroItem.punkt_1_titel || heroItem.punkt_1_tekst) {
                parsedStats.push({
                  value: typeof heroItem.punkt_1_titel === 'object' ? (heroItem.punkt_1_titel[language] || heroItem.punkt_1_titel.da || '') : (heroItem.punkt_1_titel || ''),
                  label: heroItem.punkt_1_tekst || { da: '' },
                });
              }
              if (heroItem.punkt_2_titel || heroItem.punkt_2_tekst) {
                parsedStats.push({
                  value: typeof heroItem.punkt_2_titel === 'object' ? (heroItem.punkt_2_titel[language] || heroItem.punkt_2_titel.da || '') : (heroItem.punkt_2_titel || ''),
                  label: heroItem.punkt_2_tekst || { da: '' },
                });
              }
              if (heroItem.punkt_3_titel || heroItem.punkt_3_tekst) {
                parsedStats.push({
                  value: typeof heroItem.punkt_3_titel === 'object' ? (heroItem.punkt_3_titel[language] || heroItem.punkt_3_titel.da || '') : (heroItem.punkt_3_titel || ''),
                  label: heroItem.punkt_3_tekst || { da: '' },
                });
              }
            }
            if (parsedStats.length > 0) {
              setStats(parsedStats);
            }
          }

          // Udlæs ydelser direkte fra forside samlingen
          const forsideServiceItems = forsideRes.value.filter((i) => (i.noegle && i.noegle.startsWith('ydelse_')) || (i.titel && i.titel.includes('Ydelse:')));
          if (forsideServiceItems.length > 0) {
            const sorted = [...forsideServiceItems].sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
            const mapped = sorted.map((s) => {
              let details = [];
              if (s.punkter) {
                const pts = parsePoints(s.punkter, language);
                details = pts.map((p) => p.full || p.text);
              } else {
                ['punkt_1_tekst', 'punkt_2_tekst', 'punkt_3_tekst', 'punkt_4_tekst'].forEach((pKey) => {
                  if (s[pKey]) {
                    const val = typeof s[pKey] === 'object' ? (s[pKey][language] || s[pKey].da || '') : s[pKey];
                    if (val) details.push(val);
                  }
                });
              }
              const img = Array.isArray(s.billede) ? s.billede[0] : s.billede;
              let serviceId = s.noegle;
              if (s.noegle === 'ydelse_privat') serviceId = 'privatrengoring';
              else if (s.noegle === 'ydelse_erhverv') serviceId = 'erhvervsrengoring';
              else if (s.noegle === 'ydelse_flyt') serviceId = 'flytterengoring';

              return {
                id: serviceId,
                title: s.overskrift || s.titel,
                description: s.tekst ? (typeof s.tekst === 'object' ? (s.tekst[language] || s.tekst.da || '').replace(/<[^>]*>/g, '').trim() : String(s.tekst).replace(/<[^>]*>/g, '').trim()) : '',
                details: { da: details, en: details },
                image: img || '/images/private-cleaning.png',
                link: s.knap_link || '/tjenester',
              };
            });
            setForsideServices(mapped);
          }

          // Udlæs kundeanmeldelser direkte fra forside samlingen
          const forsideReviewItems = forsideRes.value.filter((i) => (i.noegle && i.noegle.startsWith('anmeldelse_')) || (i.titel && i.titel.includes('Anmeldelse:')));
          if (forsideReviewItems.length > 0) {
            const sorted = [...forsideReviewItems].sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
            const mapped = sorted.map((r) => ({
              name: typeof r.overskrift === 'object' ? (r.overskrift[language] || r.overskrift.da || '') : (r.overskrift || r.titel),
              role: r.underoverskrift || { da: '' },
              text: r.tekst ? (typeof r.tekst === 'object' ? (r.tekst[language] || r.tekst.da || '').replace(/<[^>]*>/g, '').trim() : String(r.tekst).replace(/<[^>]*>/g, '').trim()) : '',
              rating: r.stjerner || 5,
            }));
            setTestimonials(mapped);
          }
        }

        // Om os (samlet formular uden sektionsopdeling samt bagudkompatibilitet)
        if (omOsRes.status === 'fulfilled' && Array.isArray(omOsRes.value)) {
          const singleItem = omOsRes.value.find(i => i.historie_overskrift || i.banner_billede || i.titel === 'Om os') || omOsRes.value[0];
          if (singleItem && (singleItem.historie_overskrift || singleItem.historie_tekst || singleItem.banner_billede)) {
            mappedPages['om_os'] = { ...singleItem, ...mappedPages['om_os'] };
            mappedContent['om_os'] = singleItem;

            const vanImg = singleItem.historie_billede ? (Array.isArray(singleItem.historie_billede) ? singleItem.historie_billede[0] : singleItem.historie_billede) : null;
            if (vanImg) {
              mappedImages['about_van'] = vanImg;
              mappedImages['om_os_historie'] = vanImg;
              mappedImages['om_os_historie_billede'] = vanImg;
            }
            const bannerImg = singleItem.banner_billede ? (Array.isArray(singleItem.banner_billede) ? singleItem.banner_billede[0] : singleItem.banner_billede) : null;
            if (bannerImg) {
              mappedImages['about_banner'] = bannerImg;
              mappedImages['om_os_banner'] = bannerImg;
              mappedImages['om_os_banner_billede'] = bannerImg;
            }

            mappedContent['om_os_hovedtekst'] = {
              overskrift: singleItem.historie_overskrift,
              tekst: singleItem.historie_tekst,
              knap_tekst: singleItem.historie_knap_tekst,
              knap_link: singleItem.historie_knap_link,
            };
            mappedContent['om_os_vaerdier_intro'] = {
              overskrift: singleItem.vaerdier_overskrift,
              tekst: singleItem.vaerdier_tekst,
            };
            mappedContent['om_os_privat_erhverv'] = {
              overskrift: singleItem.privat_erhverv_overskrift,
              tekst: singleItem.privat_erhverv_tekst,
            };
            mappedContent['om_os_tryghed'] = {
              overskrift: singleItem.tryghed_overskrift,
              tekst: singleItem.tryghed_tekst,
              underoverskrift: singleItem.tryghed_badge,
            };
            mappedContent['om_os_cta'] = {
              overskrift: singleItem.cta_overskrift,
              tekst: singleItem.cta_tekst,
              knap_tekst: singleItem.cta_knap_tekst,
              knap_link: singleItem.cta_knap_link,
            };
          }

          processPageItems(omOsRes.value, 'om_os', {
            historie: ['om_os_hovedtekst', 'om_os_historie'],
            historie_img: ['about_van'],
            vaerdier: ['om_os_vaerdier_intro', 'om_os_vaerdier'],
            privat_erhverv: ['om_os_privat_erhverv'],
            tryghed: ['om_os_tryghed'],
            banner: ['om_os_banner'],
            banner_img: ['about_banner'],
            cta: ['om_os_cta'],
          });
        }

        // Privatrengøring (samlet formular uden sektionsopdeling samt bagudkompatibilitet)
        if (privatRes.status === 'fulfilled' && Array.isArray(privatRes.value)) {
          const singleItem = privatRes.value.find(i => i.hero_overskrift || i.fradrag_overskrift || i.tilbud_overskrift || i.titel === 'Privatrengøring') || privatRes.value[0];
          if (singleItem && (singleItem.hero_overskrift || singleItem.hero_tekst || singleItem.fradrag_overskrift || singleItem.tilbud_overskrift)) {
            mappedPages['privatrengoring'] = { ...singleItem, ...mappedPages['privatrengoring'] };
            mappedContent['privatrengoring'] = singleItem;

            const heroImg = singleItem.hero_billede ? (Array.isArray(singleItem.hero_billede) ? singleItem.hero_billede[0] : singleItem.hero_billede) : null;
            if (heroImg) {
              mappedImages['private_hero'] = heroImg;
              mappedImages['privat_hero'] = heroImg;
              mappedImages['privat_hero_billede'] = heroImg;
            }
            const tilbudImg = singleItem.tilbud_billede ? (Array.isArray(singleItem.tilbud_billede) ? singleItem.tilbud_billede[0] : singleItem.tilbud_billede) : null;
            if (tilbudImg) {
              mappedImages['private_kitchen'] = tilbudImg;
              mappedImages['privat_tilbud'] = tilbudImg;
              mappedImages['privat_tilbud_billede'] = tilbudImg;
            }

            mappedContent['privat_hero'] = {
              overskrift: singleItem.hero_overskrift,
              underoverskrift: singleItem.hero_underoverskrift,
              tekst: singleItem.hero_tekst,
              knap_tekst: singleItem.hero_knap_tekst,
              knap_link: singleItem.hero_knap_link,
            };
            mappedContent['privat_fradrag'] = {
              overskrift: singleItem.fradrag_overskrift,
              tekst: singleItem.fradrag_tekst,
              knap_tekst: singleItem.fradrag_knap_tekst,
              knap_link: singleItem.fradrag_knap_link,
            };
            mappedContent['privat_tilbud'] = {
              overskrift: singleItem.tilbud_overskrift,
              punkter: singleItem.tilbud_punkter,
              punkt_1_titel: singleItem.tilbud_punkt_1_titel,
              punkt_1_tekst: singleItem.tilbud_punkt_1_tekst,
              punkt_2_titel: singleItem.tilbud_punkt_2_titel,
              punkt_2_tekst: singleItem.tilbud_punkt_2_tekst,
              punkt_3_titel: singleItem.tilbud_punkt_3_titel,
              punkt_3_tekst: singleItem.tilbud_punkt_3_tekst,
              punkt_4_titel: singleItem.tilbud_punkt_4_titel,
              punkt_4_tekst: singleItem.tilbud_punkt_4_tekst,
              punkt_5_titel: singleItem.tilbud_punkt_5_titel,
              punkt_5_tekst: singleItem.tilbud_punkt_5_tekst,
            };
            mappedContent['privat_tjekliste'] = {
              overskrift: singleItem.tjekliste_overskrift,
              underoverskrift: singleItem.tjekliste_underoverskrift,
              tekst: singleItem.tjekliste_tekst,
              punkter: singleItem.tjekliste_punkter,
            };
            mappedContent['privat_cta'] = {
              citat: singleItem.cta_citat,
              knap_tekst: singleItem.cta_knap_tekst,
              knap_link: singleItem.cta_knap_link,
            };
          }

          processPageItems(privatRes.value, 'privatrengoring', {
            hero: ['privat_hero'],
            hero_img: ['private_hero'],
            tilbud: ['privat_tilbud'],
            tilbud_img: ['private_kitchen'],
            fradrag: ['privat_fradrag'],
            tjekliste: ['privat_tjekliste'],
            cta: ['privat_cta'],
          });
        }

        // Erhvervsrengøring (samlet formular uden sektionsopdeling samt bagudkompatibilitet)
        if (erhvervRes.status === 'fulfilled' && Array.isArray(erhvervRes.value)) {
          const singleItem = erhvervRes.value.find(i => i.hero_overskrift || i.loesninger_overskrift || i.titel === 'Erhvervsrengøring') || erhvervRes.value[0];
          if (singleItem && (singleItem.hero_overskrift || singleItem.hero_tekst || singleItem.loesninger_overskrift)) {
            mappedPages['erhvervsrengoring'] = { ...singleItem, ...mappedPages['erhvervsrengoring'] };
            mappedContent['erhvervsrengoring'] = singleItem;

            const heroImg = singleItem.hero_billede ? (Array.isArray(singleItem.hero_billede) ? singleItem.hero_billede[0] : singleItem.hero_billede) : null;
            if (heroImg) {
              mappedImages['business_hero'] = heroImg;
              mappedImages['erhverv_hero'] = heroImg;
              mappedImages['erhverv_hero_billede'] = heroImg;
            }
            const loesningerImg = singleItem.loesninger_billede ? (Array.isArray(singleItem.loesninger_billede) ? singleItem.loesninger_billede[0] : singleItem.loesninger_billede) : null;
            if (loesningerImg) {
              mappedImages['business_office'] = loesningerImg;
              mappedImages['erhverv_loesninger'] = loesningerImg;
              mappedImages['erhverv_loesninger_billede'] = loesningerImg;
            }

            mappedContent['erhverv_hero'] = {
              overskrift: singleItem.hero_overskrift,
              underoverskrift: singleItem.hero_underoverskrift,
              tekst: singleItem.hero_tekst,
              knap_tekst: singleItem.hero_knap_tekst,
              knap_link: singleItem.hero_knap_link,
            };
            mappedContent['erhverv_loesninger'] = {
              overskrift: singleItem.loesninger_overskrift,
              punkter: singleItem.loesninger_punkter,
              punkt_1_titel: singleItem.loesninger_punkt_1_titel,
              punkt_1_tekst: singleItem.loesninger_punkt_1_tekst,
              punkt_2_titel: singleItem.loesninger_punkt_2_titel,
              punkt_2_tekst: singleItem.loesninger_punkt_2_tekst,
              punkt_3_titel: singleItem.loesninger_punkt_3_titel,
              punkt_3_tekst: singleItem.loesninger_punkt_3_tekst,
              punkt_4_titel: singleItem.loesninger_punkt_4_titel,
              punkt_4_tekst: singleItem.loesninger_punkt_4_tekst,
            };
            mappedContent['erhverv_pakke'] = {
              badge: singleItem.pakke_badge,
              underoverskrift: singleItem.pakke_badge || singleItem.pakke_underoverskrift,
              overskrift: singleItem.pakke_overskrift,
              tjekliste_underoverskrift: singleItem.pakke_underoverskrift,
              tjekliste: singleItem.pakke_tjekliste,
              punkter: singleItem.pakke_punkter,
              tekst: singleItem.pakke_tjekliste || singleItem.pakke_punkter,
            };
            mappedContent['erhverv_cta'] = {
              citat: singleItem.cta_citat,
              knap_tekst: singleItem.cta_knap_tekst,
              knap_link: singleItem.cta_knap_link,
            };
            mappedContent['erhverv_hvorfor_os'] = {
              overskrift: singleItem.fordele_overskrift,
              punkter: singleItem.fordele_punkter,
              punkt_1_titel: singleItem.fordele_punkt_1_titel,
              punkt_1_tekst: singleItem.fordele_punkt_1_tekst,
              punkt_2_titel: singleItem.fordele_punkt_2_titel,
              punkt_2_tekst: singleItem.fordele_punkt_2_tekst,
              punkt_3_titel: singleItem.fordele_punkt_3_titel,
              punkt_3_tekst: singleItem.fordele_punkt_3_tekst,
            };
            mappedContent['erhverv_fordele'] = mappedContent['erhverv_hvorfor_os'];
          }

          processPageItems(erhvervRes.value, 'erhvervsrengoring', {
            hero: ['erhverv_hero'],
            hero_img: ['business_hero'],
            loesninger: ['erhverv_loesninger'],
            loesninger_img: ['business_office'],
            pakke: ['erhverv_pakke'],
            hvorfor_os: ['erhverv_hvorfor_os', 'erhverv_fordele'],
            fordele: ['erhverv_hvorfor_os', 'erhverv_fordele'],
            cta: ['erhverv_cta'],
          });
        }

        // Tjenester
        if (tjenesterRes.status === 'fulfilled' && Array.isArray(tjenesterRes.value)) {
          const singleItem = tjenesterRes.value.find(i => i.hero_overskrift || i.privat_titel || i.titel === 'Tjenester') || tjenesterRes.value[0];
          if (singleItem && (singleItem.hero_overskrift || singleItem.privat_titel || singleItem.speciel_overskrift)) {
            mappedPages['tjenester_side'] = { ...singleItem, ...mappedPages['tjenester_side'] };
            mappedContent['tjenester_side'] = singleItem;

            const heroImg = singleItem.hero_billede ? (Array.isArray(singleItem.hero_billede) ? singleItem.hero_billede[0] : singleItem.hero_billede) : null;
            if (heroImg) {
              mappedImages['services_hero'] = heroImg;
              mappedImages['tjenester_hero'] = heroImg;
              mappedImages['tjenester_hero_billede'] = heroImg;
            }

            const privatImg = singleItem.privat_billede ? (Array.isArray(singleItem.privat_billede) ? singleItem.privat_billede[0] : singleItem.privat_billede) : null;
            if (privatImg) {
              mappedImages['service_private'] = privatImg;
              mappedImages['privat_billede'] = privatImg;
            }

            const erhvervImg = singleItem.erhverv_billede ? (Array.isArray(singleItem.erhverv_billede) ? singleItem.erhverv_billede[0] : singleItem.erhverv_billede) : null;
            if (erhvervImg) {
              mappedImages['service_business'] = erhvervImg;
              mappedImages['erhverv_billede'] = erhvervImg;
            }

            const flytImg = singleItem.flyt_billede ? (Array.isArray(singleItem.flyt_billede) ? singleItem.flyt_billede[0] : singleItem.flyt_billede) : null;
            if (flytImg) {
              mappedImages['service_move'] = flytImg;
              mappedImages['flyt_billede'] = flytImg;
            }

            mappedContent['tjenester_hero'] = {
              overskrift: singleItem.hero_overskrift,
              underoverskrift: singleItem.hero_underoverskrift,
              tekst: singleItem.hero_tekst,
              knap_tekst: singleItem.hero_knap_tekst,
              knap_link: singleItem.hero_knap_link,
            };

            mappedContent['tjenester_specielle_oensker'] = {
              overskrift: singleItem.speciel_overskrift,
              tekst: singleItem.speciel_tekst,
              knap_tekst: singleItem.speciel_knap_tekst,
              knap_link: singleItem.speciel_knap_link,
            };

            const extractServicePoints = (prefix) => {
              const points = [];
              ['1', '2', '3', '4'].forEach((num) => {
                const val = singleItem[`${prefix}_punkt_${num}`];
                if (val) {
                  let text = '';
                  if (typeof val === 'object') {
                    text = val[language] || (language === 'da' ? val.da : '');
                  } else if (language === 'da') {
                    text = val;
                  }
                  if (text && typeof text === 'string' && text.trim()) points.push(text.trim());
                }
              });
              if (points.length > 0) return points;

              // Fallback til multiline/ældre felt hvis det findes
              const multiline = singleItem[`${prefix}_punkter`];
              if (multiline) {
                const pts = parsePoints(multiline, language);
                const parsed = pts.map((p) => p.full || p.text).filter(Boolean);
                if (parsed.length > 0) return parsed;
              }

              return [
                language === 'en' ? 'Quality cleaning with focus on detail' : 'Kvalitetsrengøring med fokus på detaljen',
                language === 'en' ? 'Eco-friendly cleaning products' : 'Miljøvenlige rengøringsmidler',
                language === 'en' ? 'Experienced and insured employees' : 'Erfarne og forsikrede medarbejdere',
              ];
            };

            const defPrivat = defaultServices.find((s) => s.id === 'privatrengoring');
            const defErhverv = defaultServices.find((s) => s.id === 'erhvervsrengoring');
            const defFlyt = defaultServices.find((s) => s.id === 'flytterengoring');

            const getTjenesteTitle = (fieldVal, defService) => {
              if (!fieldVal) return defService?.title || { da: '', en: '' };
              if (typeof fieldVal === 'object') {
                return {
                  da: fieldVal.da || defService?.title?.da || '',
                  en: fieldVal.en || defService?.title?.en || fieldVal.da || '',
                };
              }
              return { da: fieldVal, en: defService?.title?.en || fieldVal };
            };

            const getTjenesteDesc = (fieldVal, defService) => {
              if (!fieldVal) return language === 'en' ? defService?.description?.en : defService?.description?.da;
              if (typeof fieldVal === 'object') {
                if (fieldVal[language]) return fieldVal[language].replace(/<[^>]*>/g, '').trim();
                if (language === 'en' && defService?.description?.en) return defService.description.en;
                return (fieldVal.da || '').replace(/<[^>]*>/g, '').trim();
              }
              if (language === 'en' && defService?.description?.en) return defService.description.en;
              return String(fieldVal).replace(/<[^>]*>/g, '').trim();
            };

            const singleServices = [
              {
                id: 'privatrengoring',
                title: getTjenesteTitle(singleItem.privat_titel, defPrivat),
                description: getTjenesteDesc(singleItem.privat_tekst, defPrivat),
                details: { da: extractServicePoints('privat'), en: extractServicePoints('privat') },
                image: privatImg || '/images/private-cleaning.png',
                link: singleItem.privat_knap_2_link || '/privatrengoring',
                orderLink: singleItem.privat_knap_link || '/kontakt',
                orderText: singleItem.privat_knap_tekst || { da: 'Få et tilbud', en: 'Get a quote' },
                readMoreLink: singleItem.privat_knap_2_link || '/privatrengoring',
                readMoreText: singleItem.privat_knap_2_tekst || { da: 'Læs mere om privat', en: 'Read more about private' },
              },
              {
                id: 'erhvervsrengoring',
                title: getTjenesteTitle(singleItem.erhverv_titel, defErhverv),
                description: getTjenesteDesc(singleItem.erhverv_tekst, defErhverv),
                details: { da: extractServicePoints('erhverv'), en: extractServicePoints('erhverv') },
                image: erhvervImg || '/images/business-cleaning.png',
                link: singleItem.erhverv_knap_2_link || '/erhvervsrengoring',
                orderLink: singleItem.erhverv_knap_link || '/kontakt',
                orderText: singleItem.erhverv_knap_tekst || { da: 'Få et tilbud', en: 'Get a quote' },
                readMoreLink: singleItem.erhverv_knap_2_link || '/erhvervsrengoring',
                readMoreText: singleItem.erhverv_knap_2_tekst || { da: 'Læs mere om erhverv', en: 'Read more about business' },
              },
              {
                id: 'flytterengoring',
                title: getTjenesteTitle(singleItem.flyt_titel, defFlyt),
                description: getTjenesteDesc(singleItem.flyt_tekst, defFlyt),
                details: { da: extractServicePoints('flyt'), en: extractServicePoints('flyt') },
                image: flytImg || '/images/move-cleaning.png',
                link: singleItem.flyt_knap_2_link || '/kontakt',
                orderLink: singleItem.flyt_knap_link || '/kontakt',
                orderText: singleItem.flyt_knap_tekst || { da: 'Få et tilbud', en: 'Get a quote' },
                readMoreLink: singleItem.flyt_knap_2_link || '/kontakt',
                readMoreText: singleItem.flyt_knap_2_tekst || { da: 'Kontakt os', en: 'Contact us' },
              },
            ];
            setTjenesterServices(singleServices);
          }

          processPageItems(tjenesterRes.value, 'tjenester_side', {
            hero: ['tjenester_hero'],
            hero_img: ['services_hero'],
            specielle_oensker: ['tjenester_specielle_oensker'],
            cta: ['tjenester_cta'],
          });

          // Udlæs ydelser direkte fra tjenester_side samlingen som fallback hvis multi-item
          const tjenesterServiceItems = tjenesterRes.value.filter((i) => (i.noegle && i.noegle.startsWith('ydelse_')) || (i.titel && i.titel.includes('Ydelse:')));
          if (tjenesterServiceItems.length > 0 && (!singleItem || (!singleItem.privat_titel && !singleItem.hero_overskrift))) {
            const sorted = [...tjenesterServiceItems].sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
            const mapped = sorted.map((s) => {
              let details = [];
              if (s.punkter) {
                const pts = parsePoints(s.punkter, language);
                details = pts.map((p) => p.full || p.text);
              } else {
                ['punkt_1_tekst', 'punkt_2_tekst', 'punkt_3_tekst', 'punkt_4_tekst'].forEach((pKey) => {
                  if (s[pKey]) {
                    const val = typeof s[pKey] === 'object' ? (s[pKey][language] || s[pKey].da || '') : s[pKey];
                    if (val) details.push(val);
                  }
                });
              }
              const img = Array.isArray(s.billede) ? s.billede[0] : s.billede;
              let serviceId = s.noegle;
              if (s.noegle === 'ydelse_privat') serviceId = 'privatrengoring';
              else if (s.noegle === 'ydelse_erhverv') serviceId = 'erhvervsrengoring';
              else if (s.noegle === 'ydelse_flyt') serviceId = 'flytterengoring';

              return {
                id: serviceId,
                title: s.overskrift || s.titel,
                description: s.tekst ? (typeof s.tekst === 'object' ? (s.tekst[language] || s.tekst.da || '').replace(/<[^>]*>/g, '').trim() : String(s.tekst).replace(/<[^>]*>/g, '').trim()) : '',
                details: { da: details, en: details },
                image: img || '/images/private-cleaning.png',
                link: s.knap_2_link || s.knap_link || '/tjenester',
                orderLink: s.knap_link || '/kontakt',
                orderText: s.knap_tekst || { da: 'Bestil nu', en: 'Order now' },
                readMoreLink: s.knap_2_link || s.knap_link,
                readMoreText: s.knap_2_tekst || { da: 'Læs mere', en: 'Read more' },
              };
            });
            setTjenesterServices(mapped);
          }
        }

        // Kontakt
        if (kontaktRes.status === 'fulfilled') {
          if (Array.isArray(kontaktRes.value)) {
            const singleItem = kontaktRes.value.find(i => i.hero_overskrift || i.titel === 'Kontakt' || i.titel === 'Kontaktside') || kontaktRes.value[0];
            if (singleItem && (singleItem.hero_overskrift || singleItem.hero_tekst || singleItem.hero_billede || singleItem.info_overskrift)) {
              mappedPages['kontakt_side'] = { ...singleItem, ...mappedPages['kontakt_side'] };
              mappedContent['kontakt_side'] = singleItem;

              const heroImg = singleItem.hero_billede ? (Array.isArray(singleItem.hero_billede) ? singleItem.hero_billede[0] : singleItem.hero_billede) : null;
              if (heroImg) {
                mappedImages['contact_hero'] = heroImg;
                mappedImages['kontakt_hero'] = heroImg;
              }

              mappedContent['kontakt_hero'] = {
                underoverskrift: singleItem.hero_underoverskrift,
                overskrift: singleItem.hero_overskrift,
                tekst: singleItem.hero_tekst,
                knap_tekst: singleItem.hero_knap_tekst,
                knap_link: singleItem.hero_knap_link,
              };
              mappedContent['kontakt_info_sektion'] = {
                overskrift: singleItem.info_overskrift,
                underoverskrift: singleItem.info_respons_titel,
                tekst: singleItem.info_respons_tekst,
              };
              mappedContent['kontakt_info'] = mappedContent['kontakt_info_sektion'];
              mappedContent['kontakt_formular'] = {
                overskrift: singleItem.formular_overskrift,
                tekst: singleItem.formular_tekst,
                knap_tekst: singleItem.formular_knap_tekst,
              };
            }
          }

          processPageItems(kontaktRes.value, 'kontakt_side', {
            hero: ['kontakt_hero'],
            hero_img: ['contact_hero'],
            info: ['kontakt_info_sektion', 'kontakt_info'],
            formular: ['kontakt_formular'],
          });
        }

        // Galleri
        if (galleriRes.status === 'fulfilled') {
          processPageItems(galleriRes.value, 'galleri_side', {
            hero: ['galleri_hero'],
            hero_img: ['gallery_hero'],
          });
        }

        setImages(mappedImages);
        setContent(mappedContent);
        setPages(mappedPages);

        // 4. Virksomhedsoplysninger
        if (companyRes.status === 'fulfilled' && Array.isArray(companyRes.value) && companyRes.value.length > 0) {
          const c = companyRes.value[0];
          const logoSrc = Array.isArray(c.logo) ? c.logo[0] : c.logo;
          const defaultOpening = { da: 'Mandag — Fredag: 08:00 - 16:00', en: 'Monday — Friday: 08:00 - 16:00' };
          const defaultFooter = {
            da: 'Professionel rengøring til hjem og erhverv i Billund, Varde og Vejle kommune. Vi vægter kvalitet, tryghed og miljø højt.',
            en: 'Professional cleaning for home and business in Billund, Varde and Vejle municipality. We value quality, safety and the environment highly.'
          };
          const rawOpening = c.aabningstider;
          const openingHours = typeof rawOpening === 'object'
            ? { da: rawOpening.da || defaultOpening.da, en: rawOpening.en || defaultOpening.en }
            : (rawOpening ? { da: rawOpening, en: defaultOpening.en } : defaultOpening);
          const rawFooter = c.footer_tekst;
          const footerText = typeof rawFooter === 'object'
            ? { da: rawFooter.da || defaultFooter.da, en: rawFooter.en || defaultFooter.en }
            : (rawFooter ? { da: rawFooter, en: defaultFooter.en } : defaultFooter);

          setCompanyInfo({
            name: c.firmanavn || defaultContactInfo.name,
            owner: c.ejer || defaultContactInfo.owner,
            address: c.adresse || defaultContactInfo.address,
            zipCity: c.postnr_og_by || defaultContactInfo.zipCity,
            phone: c.telefon || defaultContactInfo.phone,
            email: c.email || defaultContactInfo.email,
            facebook: c.facebook || defaultContactInfo.facebook,
            openingHours,
            logo: logoSrc || '/images/ftr_logo_v1.png',
            footerText,
          });
          if (logoSrc) {
            mappedImages['logo'] = logoSrc;
          }
          if (footerText) {
            mappedContent['footer_tekst'] = {
              tekst: footerText,
              noegle: 'footer_tekst',
            };
          }
        }

        // Kundeudtalelser
        if (testimonialsRes.status === 'fulfilled' && Array.isArray(testimonialsRes.value) && testimonialsRes.value.length > 0) {
          const sortedTestimonials = [...testimonialsRes.value].sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
          setTestimonials(sortedTestimonials.map((t) => {
            const def = defaultTestimonials.find((dt) => dt.name === t.navn);
            const roleObj = typeof t.rolle === 'object' ? t.rolle : { da: t.rolle };
            const textObj = typeof t.anmeldelse === 'object' ? t.anmeldelse : { da: t.anmeldelse };
            return {
              name: t.navn || (def?.name ?? ''),
              role: {
                da: roleObj.da || def?.role?.da || '',
                en: roleObj.en || def?.role?.en || roleObj.da || '',
              },
              text: {
                da: textObj.da || def?.text?.da || '',
                en: textObj.en || def?.text?.en || textObj.da || '',
              },
              rating: t.stjerner || 5,
            };
          }));
        }
      } catch (err) {
        console.warn('Could not load CMS content, using fallbacks:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadCmsData();

    return () => {
      isMounted = false;
    };
  }, [language]);

  const getImage = (key, fallback, options = {}) => {
    const rawUrl = images[key] || fallback;
    return getOptimizedImageUrl(rawUrl, options);
  };

  const resolvePointField = (item, field) => {
    if (!item) return null;
    const ptsSource = item.tilbud_punkter || item.tjekliste_punkter || item.punkter;
    if (!ptsSource) return null;
    const match = field.match(/^(?:tilbud_|tjekliste_)?punkt_(\d+)_(titel|tekst)$/);
    if (!match) return null;
    const index = parseInt(match[1], 10) - 1;
    const type = match[2];
    const pts = parsePoints(ptsSource, language);
    if (index >= 0 && index < pts.length) {
      if (type === 'titel') return pts[index].title || '';
      if (type === 'tekst') return pts[index].text || pts[index].full || '';
    }
    return null;
  };

  const getText = (key, field, fallback) => {
    const item = content[key];
    if (!item) return fallback;
    let val = item[field];
    if (val === undefined || val === null || val === '') {
      const resolved = resolvePointField(item, field);
      if (resolved !== null && resolved !== '') {
        val = resolved;
      } else {
        return fallback;
      }
    }
    let strVal = val;
    if (typeof val === 'object') {
      if (val[language] !== undefined && val[language] !== null && val[language] !== '') {
        strVal = val[language];
      } else if (language !== 'da' && fallback !== undefined && fallback !== null && fallback !== '') {
        return fallback;
      } else {
        strVal = val.da || Object.values(val)[0] || fallback;
      }
    }
    if (typeof strVal === 'string') {
      const stripped = strVal
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, ' ')
        .replace(/[^\S\r\n]+/g, ' ')
        .replace(/\n\s+/g, '\n')
        .replace(/\s+\n/g, '\n')
        .replace(/\n+/g, '\n')
        .trim();
      return stripped || fallback;
    }
    return strVal;
  };

  const getHtml = (key, field, fallback) => {
    const item = content[key];
    if (!item) return fallback;
    const val = item[field];
    if (val === undefined || val === null || val === '') return fallback;
    if (typeof val === 'object') {
      if (val[language] !== undefined && val[language] !== null && val[language] !== '') {
        return val[language];
      }
      if (language !== 'da' && fallback !== undefined && fallback !== null && fallback !== '') {
        return fallback;
      }
      return val.da || Object.values(val)[0] || fallback;
    }
    return val;
  };

  const getPageText = (page, section, field, fallback) => {
    const pageObj = pages[page];
    if (!pageObj) return getText(`${page}_${section}`, field, fallback);
    const item = pageObj[section];
    if (!item) return getText(`${page}_${section}`, field, fallback);
    let val = item[field];
    if (val === undefined || val === null || val === '') {
      const resolved = resolvePointField(item, field);
      if (resolved !== null && resolved !== '') {
        val = resolved;
      } else {
        return fallback;
      }
    }
    let strVal = val;
    if (typeof val === 'object') {
      if (val[language] !== undefined && val[language] !== null && val[language] !== '') {
        strVal = val[language];
      } else if (language !== 'da' && fallback !== undefined && fallback !== null && fallback !== '') {
        return fallback;
      } else {
        strVal = val.da || Object.values(val)[0] || fallback;
      }
    }
    if (typeof strVal === 'string') {
      const stripped = strVal
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, ' ')
        .replace(/[^\S\r\n]+/g, ' ')
        .replace(/\n\s+/g, '\n')
        .replace(/\s+\n/g, '\n')
        .replace(/\n+/g, '\n')
        .trim();
      return stripped || fallback;
    }
    return strVal;
  };

  const getPageHtml = (page, section, field, fallback) => {
    const pageObj = pages[page];
    if (!pageObj) return getHtml(`${page}_${section}`, field, fallback);
    const item = pageObj[section];
    if (!item) return getHtml(`${page}_${section}`, field, fallback);
    const val = item[field];
    if (val === undefined || val === null || val === '') return fallback;
    if (typeof val === 'object') {
      if (val[language] !== undefined && val[language] !== null && val[language] !== '') {
        return val[language];
      }
      if (language !== 'da' && fallback !== undefined && fallback !== null && fallback !== '') {
        return fallback;
      }
      return val.da || Object.values(val)[0] || fallback;
    }
    return val;
  };

  const getPageImage = (page, section, fallback, options = {}) => {
    const pageObj = pages[page];
    if (pageObj && pageObj[section] && pageObj[section].billede) {
      const img = pageObj[section].billede;
      const src = Array.isArray(img) ? img[0] : img;
      if (src) return getOptimizedImageUrl(src, options);
    }
    return getImage(`${page}_${section}`, fallback, options);
  };

  const getPoints = (key, fallback = []) => {
    const item = content[key];
    if (item && item.punkter) {
      const pts = parsePoints(item.punkter, language, fallback);
      if (pts.length > 0) return pts;
    }
    // Fallback til traditionelle punkt_1..4 felter
    if (item) {
      const legacy = [];
      [1, 2, 3, 4].forEach((n) => {
        const tField = item[`punkt_${n}_titel`];
        const txtField = item[`punkt_${n}_tekst`];
        if (tField || txtField) {
          const title = typeof tField === 'object' ? (tField[language] || (language === 'da' ? tField.da : '')) : (language === 'da' ? tField : '');
          const text = typeof txtField === 'object' ? (txtField[language] || (language === 'da' ? txtField.da : '')) : (language === 'da' ? txtField : '');
          if (title || text) {
            legacy.push({ title, text, full: title ? `${title}: ${text}` : text });
          }
        }
      });
      if (legacy.length > 0) return legacy;
    }
    return fallback;
  };

  const getPagePoints = (page, section, fallback = []) => {
    const pageObj = pages[page];
    if (pageObj && pageObj[section]) {
      const item = pageObj[section];
      if (item.punkter) {
        const pts = parsePoints(item.punkter, language, fallback);
        if (pts.length > 0) return pts;
      }
    }
    return getPoints(`${page}_${section}`, fallback);
  };

  return (
    <CmsContentContext.Provider
      value={{
        getImage,
        getText,
        getHtml,
        getPageText,
        getPageHtml,
        getPageImage,
        getPoints,
        getPagePoints,
        images,
        content,
        pages,
        companyInfo,
        stats,
        testimonials,
        services,
        forsideServices,
        tjenesterServices,
        loading,
      }}
    >
      {children}
    </CmsContentContext.Provider>
  );
}

export function useCmsImages() {
  return useContext(CmsContentContext);
}

export function useCmsContent() {
  return useContext(CmsContentContext);
}
